import type { ReactNode } from 'react';

interface CardProps {
  children: ReactNode;
  variant?: 'default' | 'metric' | 'list' | 'hero';
  className?: string;
}

interface CardHeaderProps {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
}

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

const Card = ({ children, variant = 'default', className = '' }: CardProps) => {
  const baseClasses = 'rounded-lg border';
  
  const getVariantStyles = (variant: string) => {
    switch(variant) {
      case 'metric':
        return {
          backgroundColor: 'var(--oro-surface-alt)',
          border: '1px solid var(--oro-border)',
          boxShadow: 'var(--shadow-soft)'
        };
      case 'list':
        return {
          backgroundColor: 'var(--oro-surface)',
          border: '1px solid var(--oro-border)',
          boxShadow: 'var(--shadow-card)'
        };
      case 'hero':
        return {
          backgroundColor: 'var(--oro-surface)',
          border: '1px solid var(--oro-border)',
          boxShadow: 'var(--shadow-glow)'
        };
      default:
        return {
          backgroundColor: 'var(--oro-surface)',
          border: '1px solid var(--oro-border)',
          boxShadow: 'var(--shadow-card)'
        };
    }
  };

  const variantClasses = {
    default: 'p-4 md:p-5',
    metric: 'p-4 md:p-5',
    list: 'p-0 divide-y',
    hero: 'p-5 md:p-6'
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${className}`;

  return (
    <div className={classes} style={getVariantStyles(variant)}>
      {children}
    </div>
  );
};

const CardHeader = ({ title, subtitle, actions, className = '' }: CardHeaderProps) => {
  if (!title && !subtitle && !actions) return null;

  return (
    <div className={`flex items-start justify-between gap-4 ${className}`}>
      <div className="min-w-0 flex-1">
        {title && (
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
        )}
        {subtitle && (
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
};

const CardFooter = ({ children, className = '' }: CardFooterProps) => {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {children}
    </div>
  );
};

// List item component for use within list variant cards
interface CardListItemProps {
  children: ReactNode;
  className?: string;
}

const CardListItem = ({ children, className = '' }: CardListItemProps) => {
  return (
    <div className={`flex items-center justify-between gap-3 py-2.5 px-4 md:px-5 ${className}`}>
      {children}
    </div>
  );
};

export default Card;
export { CardHeader, CardFooter, CardListItem };